import { PartySocket } from 'partysocket';
import { readSettings } from './settings';
import { getOrCreateUserToken } from './coop.svelte';
import { showToast } from './toast';

export type Friend = {
	userId: string;
	username: string;
	avatar: string;
	since: number;
	online?: boolean;
	currentGame?: GameType | null;
	currentMode?: PlayMode | null;
	featuredBadge?: string;
};
export type PendingRequest = { userId: string; username: string; avatar: string; at: number };
export type GameType = 'word-guess' | 'word-group' | 'word-build' | 'word-find' | 'word-cross' | 'word-cross-m' | 'word-crypto' | 'word-speed' | 'num-fill' | 'word-tower';
export type PlayMode = 'Solo' | 'Co-op';
export type GameInvite = {
	userId: string;
	username: string;
	avatar: string;
	gameType: GameType;
	roomId: string;
	dateKey: string;
	at: number;
	featuredBadge?: string;
};

export const GAME_LABELS: Record<GameType, string> = {
	'word-guess': 'Word Guess',
	'word-group': 'Word Group',
	'word-build': 'Word Build',
	'word-find': 'Word Find',
	'word-cross': 'Word Cross S',
	'word-cross-m': 'Word Cross M',
	'word-crypto': 'Word Crypto',
	'word-speed': 'Word Speed',
	'num-fill': 'Num Fill',
	'word-tower': 'Word Tower'
};

export type FriendsState = {
	friends: Friend[];
	incoming: PendingRequest[];
	outgoing: PendingRequest[];
	incomingInvite: GameInvite | null;
	outgoingInvite: GameInvite | null;
};

const PARTYKIT_HOST = (import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ?? (import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');

const EMPTY: FriendsState = {
	friends: [],
	incoming: [],
	outgoing: [],
	incomingInvite: null,
	outgoingInvite: null
};

export function createFriends(userId: string) {
	const state = $state<FriendsState>({ ...EMPTY });
	let connected = $state(false);
	let synced = $state(false);
	let error = $state<string | null>(null);
	let refreshing = $state(false);
	let currentGame: GameType | null = null;
	let currentMode: PlayMode | null = null;

	let socket: PartySocket | null = null;
	let onStorage: (() => void) | null = null;

	// Track per-friend online state to toast on offline→online transitions.
	// Seeded on the first state sync so we don't fire a burst on connect.
	const lastOnline = new Map<string, boolean>();
	let presenceInit = false;

	if (typeof window !== 'undefined') {
		socket = new PartySocket({
			host: PARTYKIT_HOST,
			party: 'user',
			room: userId
		});

		const sendHello = () => {
			const s = readSettings();
			const sharedGame = s.shareGameStatus ? currentGame : null;
			socket?.send(
				JSON.stringify({
					type: 'hello',
					token: getOrCreateUserToken(),
					appearOffline: s.appearOffline,
					currentGame: sharedGame,
					currentMode: sharedGame ? currentMode : null,
					profile: {
						username: s.username || '',
						avatar: s.avatar || '',
						featuredBadge: s.featuredBadge || ''
					}
				})
			);
		};

		let everConnected = false;
		let connectTimeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
			if (!everConnected) error = 'Could not reach the friends server. Check your connection.';
		}, 8000);

		socket.addEventListener('open', () => {
			connected = true;
			everConnected = true;
			error = null;
			if (connectTimeout) {
				clearTimeout(connectTimeout);
				connectTimeout = null;
			}
			sendHello();
		});

		socket.addEventListener('error', () => {
			if (!everConnected) error = 'Could not reach the friends server. Check your connection.';
		});

		// Re-sync profile if username/avatar changes locally (other tabs / settings modal)
		onStorage = () => {
			if (socket?.readyState === WebSocket.OPEN) sendHello();
		};
		window.addEventListener('storage', onStorage);

		socket.addEventListener('close', () => {
			const wasConnected = connected;
			connected = false;
			if (wasConnected) error = 'Lost connection to the friends server. Trying to reconnect…';
		});

		socket.addEventListener('message', (event) => {
			try {
				const msg = JSON.parse(event.data);
				if (msg.type === 'state') {
					const nextFriends: Friend[] = msg.friends ?? [];
					// Toast friends that just came online (skip the initial sync).
					for (const f of nextFriends) {
						const isOnline = !!f.online;
						const prev = lastOnline.get(f.userId);
						if (presenceInit && prev === false && isOnline && readSettings().friendOnlineToasts) {
							showToast(`${f.username || 'A friend'} is online`, 'success');
						}
						lastOnline.set(f.userId, isOnline);
					}
					for (const id of [...lastOnline.keys()]) {
						if (!nextFriends.some((f) => f.userId === id)) lastOnline.delete(id);
					}
					presenceInit = true;
					// Mutate fields individually so deep-proxy reactivity fires
					// reliably across consumers.
					state.friends = nextFriends;
					state.incoming = msg.incoming ?? [];
					state.outgoing = msg.outgoing ?? [];
					state.incomingInvite = msg.incomingInvite ?? null;
					state.outgoingInvite = msg.outgoingInvite ?? null;
					synced = true;
				} else if (msg.type === 'error') {
					error = msg.message;
				}
			} catch {
				/* ignore */
			}
		});
	}

	function send(payload: object) {
		// Every WS message carries the auth token so the server can verify (or
		// TOFU-adopt) on a per-message basis. Adding it once here covers every
		// action method below.
		socket?.send(JSON.stringify({ ...payload, token: getOrCreateUserToken() }));
	}

	function clearError() {
		error = null;
	}

	return {
		get state() {
			return state;
		},
		get connected() {
			return connected;
		},
		get synced() {
			return synced;
		},
		get error() {
			return error;
		},
		get refreshing() {
			return refreshing;
		},
		clearError,
		refresh() {
			if (refreshing) return;
			refreshing = true;
			send({ type: 'refresh' });
			setTimeout(() => {
				refreshing = false;
			}, 1500);
		},
		setPresence(gameType: GameType | null, playMode: PlayMode | null = null) {
			if (currentGame === gameType && currentMode === playMode) return;
			currentGame = gameType;
			currentMode = playMode;
			if (socket?.readyState === WebSocket.OPEN) {
				const s = readSettings();
				const sharedGame = s.shareGameStatus ? currentGame : null;
				send({
					type: 'hello',
					currentGame: sharedGame,
					currentMode: sharedGame ? currentMode : null,
					profile: {
						username: s.username || '',
						avatar: s.avatar || '',
						featuredBadge: s.featuredBadge || ''
					}
				});
			}
		},
		sendRequest(toUsername: string) {
			send({ type: 'sendRequest', toUsername });
		},
		accept(fromUserId: string) {
			send({ type: 'accept', fromUserId });
		},
		decline(fromUserId: string) {
			send({ type: 'decline', fromUserId });
		},
		cancel(toUserId: string) {
			send({ type: 'cancel', toUserId });
		},
		remove(userId: string) {
			send({ type: 'remove', userId });
		},
		invitePlay(toUserId: string, gameType: GameType, dateKey: string) {
			send({ type: 'invitePlay', toUserId, gameType, dateKey });
		},
		inviteToRoom(toUserId: string, gameType: GameType, dateKey: string, roomId: string) {
			send({ type: 'inviteToRoom', toUserId, gameType, dateKey, roomId });
		},
		acceptInvite() {
			send({ type: 'acceptInvite' });
		},
		declineInvite() {
			send({ type: 'declineInvite' });
		},
		cancelInvite() {
			send({ type: 'cancelInvite' });
		},
		wipe() {
			send({ type: 'wipe' });
		},
		disconnect() {
			if (typeof window !== 'undefined' && onStorage) {
				window.removeEventListener('storage', onStorage);
			}
			socket?.close();
			socket = null;
		}
	};
}

export type Friends = ReturnType<typeof createFriends>;
